import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { formatINR } from "@/utils/app-formatters";

import { lopAmountFromComponents } from "./payroll-lop-explainer";
import {
  buildPayslipTables,
  formatPayslipAmountInWords,
  formatPayslipWageMonth,
  type PayslipAmountRow,
  type PayslipComponentLike,
} from "./payroll-payslip-format";

export type PayrollSalarySlipLine = {
  employeeId: number;
  employee?: { id: number; name: string } | null;
  employeeName?: string | null;
  monthlyGross: number;
  lopDays: number;
  earningsAmount: number;
  deductionsAmount: number;
  netPay: number;
  components?: PayslipComponentLike[] | null;
};

export type PayrollSalarySlipPeriod = {
  yearMonth: string;
};

export type PayrollSalarySlipProps = {
  tenantName: string;
  trustName?: string;
  logoUrl?: string;
  period: PayrollSalarySlipPeriod;
  line: PayrollSalarySlipLine;
};

function formatCount(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return String(value);
}

function AmountTable({
  title,
  rows,
  total,
  footerLabel,
}: {
  title: string;
  rows: PayslipAmountRow[];
  total: number;
  footerLabel: string;
}) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell>Particulars</TableCell>
            <TableCell align="right">Amount</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2}>
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>
                  <Typography variant="body2">{row.label}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{formatINR(row.amount)}</Typography>
                </TableCell>
              </TableRow>
            ))
          )}
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{footerLabel}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>
              {formatINR(total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}

function DetailCell({ label, value }: { label: string; value: string }) {
  return (
    <TableCell sx={{ width: "50%", verticalAlign: "top" }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </TableCell>
  );
}

/**
 * Print-friendly standard salary slip: company header, employee details, earnings | deductions.
 */
export function PayrollSalarySlip({
  tenantName,
  trustName,
  logoUrl,
  period,
  line,
}: PayrollSalarySlipProps) {
  const tables = buildPayslipTables({
    components: line.components,
    earningsAmount: line.earningsAmount,
    deductionsAmount: line.deductionsAmount,
    lopDays: line.lopDays,
  });

  const employeeName = line.employee?.name ?? line.employeeName ?? `Employee #${line.employeeId}`;
  const wageMonth = formatPayslipWageMonth(period.yearMonth);
  const company = tenantName.trim() || "—";
  const lopAmount = lopAmountFromComponents(line.components);
  const netInWords = formatPayslipAmountInWords(line.netPay);

  return (
    <Box
      sx={{
        p: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: "divider" }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={company !== "—" ? `${company} logo` : "Logo"}
            sx={{ width: 72, height: "auto", maxHeight: 72, objectFit: "contain" }}
          />
        ) : null}
        <Box sx={{ flex: 1, textAlign: logoUrl ? "left" : "center" }}>
          <Typography variant="h6" fontWeight={700} component="div">
            {company}
          </Typography>
          {trustName?.trim() ? (
            <Typography variant="body2" color="text.secondary">
              {trustName.trim()}
            </Typography>
          ) : null}
          <Typography variant="subtitle1" sx={{ mt: 0.5 }} fontWeight={600}>
            Salary Slip
          </Typography>
        </Box>
      </Stack>

      <Table size="small" sx={{ mb: 2, border: 1, borderColor: "divider" }}>
        <TableBody>
          <TableRow>
            <DetailCell label="Employee" value={employeeName} />
            <DetailCell label="Employee ID" value={`#${line.employeeId}`} />
          </TableRow>
          <TableRow>
            <DetailCell label="Wage month" value={wageMonth} />
            <DetailCell label="Monthly gross" value={formatINR(line.monthlyGross)} />
          </TableRow>
          <TableRow>
            <DetailCell label="LOP days" value={formatCount(line.lopDays)} />
            <DetailCell label="LOP amount" value={lopAmount != null ? formatINR(lopAmount) : "—"} />
          </TableRow>
        </TableBody>
      </Table>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="stretch"
        sx={{ mb: 2 }}
      >
        <AmountTable
          title="Earnings"
          rows={tables.earnings}
          total={tables.totalEarnings}
          footerLabel="Total earnings"
        />
        <AmountTable
          title="Deductions"
          rows={tables.deductions}
          total={tables.totalDeductions}
          footerLabel="Total deductions"
        />
      </Stack>

      <Table size="small" sx={{ border: 1, borderColor: "divider" }}>
        <TableBody>
          <TableRow sx={{ bgcolor: "action.hover" }}>
            <TableCell>
              <Typography fontWeight={700}>Net pay</Typography>
            </TableCell>
            <TableCell align="right">
              <Typography fontWeight={700}>{formatINR(line.netPay)}</Typography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={2}>
              <Typography variant="caption" color="text.secondary" component="div">
                Amount in words
              </Typography>
              <Typography variant="body2">{netInWords}</Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
